import createStore from './createStore';
import { ModalProps } from '@lwt-helix/modal';
import { Localization } from '../types';
import { ReactNode } from 'react';
import { defaultError } from '../localization/default/modal-errors-en';

export interface AppStore {
    culture: string;
    auth0: {
        queryStringParams: {
            clientId?: string;
            state?: string;
            redirectUri?: string;
            clientName?: string;
        };
    };
    modal: ModalProps;
    localization: Localization;
    formValidationErrors?: {
        [errorType: string]: string | { [deepProp: string]: string }[];
    };
    modalErrors: { [errorCode: number]: ModalProps };
}

export interface AppStoreApi extends Omit<AppStore, 'localization'> {
    setCulture: (culture: string) => void;
    setQueryStringParams: (
        clientId: string,
        state: string,
        redirectUri: string,
        clientName: string
    ) => void;
    showModal: ({ ...props }: ModalProps) => void;
    dismissModal: () => void;
    getTranslation: (resource: string) => ReactNode;
    setLocalization: (resources: Localization) => void;
    setFormValidationErrors: (errors: {
        [errorType: string]: string | { [deepProp: string]: string }[];
    }) => void;
    setModalErrors: (errors: { [errorCode: number]: ModalProps }) => void;
}

const getCulture = (): string => {
    const browserCulture =
        (navigator.languages && navigator.languages[0]) || navigator.language;
    return browserCulture.startsWith('en-') ? 'en' : browserCulture;
};

const { StoreProvider: AppStoreProvider, useStore: useAppStore } = createStore<
    AppStore,
    AppStoreApi
>(
    ({ state, setState }): AppStoreApi => {
        const setCulture = (culture: string): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    culture: culture
                })
            );
        };

        const setQueryStringParams = (
            clientId: string,
            state: string,
            redirectUri: string,
            clientName: string
        ): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    auth0: {
                        queryStringParams: {
                            clientId: clientId,
                            state: state,
                            redirectUri: redirectUri,
                            clientName: clientName
                        }
                    }
                })
            );
        };

        const dismissModal = (): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    modal: {
                        show: false
                    }
                })
            );
        };

        const showModal = ({ ...props }: ModalProps): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    modal: {
                        ...props,
                        show: true,
                        onClose: props.onClose || dismissModal
                    }
                })
            );
        };

        const getTranslation = (resource: string): ReactNode => {
            return (
                state.localization.tenant[resource] ||
                state.localization.default[resource]
            );
        };

        const setLocalization = (resources: Localization): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    localization: resources
                })
            );
        };

        const setFormValidationErrors = (errors: {
            [errorType: string]: string | { [deepProp: string]: string }[];
        }): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    formValidationErrors: errors
                })
            );
        };

        const setModalErrors = (errors: {
            [errorCode: number]: ModalProps;
        }): void => {
            setState(
                (prevState: AppStore): AppStore => ({
                    ...prevState,
                    modalErrors: errors
                })
            );
        };

        const culture = state.culture;
        const auth0 = state.auth0;
        const modal = state.modal;
        const formValidationErrors = state.formValidationErrors;
        const modalErrors = state.modalErrors;

        return {
            culture,
            auth0,
            modal,
            formValidationErrors,
            modalErrors,
            setCulture,
            setQueryStringParams,
            showModal,
            dismissModal,
            getTranslation,
            setLocalization,
            setFormValidationErrors,
            setModalErrors
        };
    },
    {
        culture: getCulture(),
        auth0: {
            queryStringParams: {}
        },
        modal: {
            show: false
        },
        localization: {
            default: {},
            tenant: {}
        },
        modalErrors: {
            0: defaultError
        }
    }
);

export { AppStoreProvider, useAppStore };
